import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatEuro, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";

const EMPTY_LINE = { designation: "", description: "", quantite: 1, unite: "u", prix_unitaire: 0, tva: 0 };
const newLine = () => ({ ...EMPTY_LINE, _uid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });
const withUids = (lignes) => (lignes || []).map((l) => (l._uid ? l : { ...l, _uid: `l-${Math.random().toString(36).slice(2, 9)}` }));

export default function DevisEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && id !== "new" && id !== "import" && id !== "nouveau";

  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    titre: "",
    objet: "",
    lignes: [newLine()],
    conditions: "",
    validite_jours: 30,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/admin/devis/${id}`)
      .then((r) => setForm({ ...r.data, lignes: r.data.lignes?.length ? withUids(r.data.lignes) : [newLine()] }))
      .catch(() => toast.error("Devis introuvable"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const totals = (() => {
    const ht = form.lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire || 0), 0);
    const tva = form.lignes.reduce(
      (s, l) => s + (Number(l.quantite || 0) * Number(l.prix_unitaire || 0) * Number(l.tva || 0)) / 100,
      0,
    );
    return { ht, tva, ttc: ht + tva };
  })();

  const setLine = (i, k, v) => {
    setForm((s) => {
      const lignes = [...s.lignes];
      lignes[i] = { ...lignes[i], [k]: v };
      return { ...s, lignes };
    });
  };

  const addLine = () => setForm((s) => ({ ...s, lignes: [...s.lignes, newLine()] }));
  const removeLine = (i) =>
    setForm((s) => ({ ...s, lignes: s.lignes.length > 1 ? s.lignes.filter((_, idx) => idx !== i) : s.lignes }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Sélectionnez un client.");
      return;
    }
    if (!form.titre) {
      toast.error("Le titre est requis.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lignes: form.lignes.map(({ _uid, ...l }) => ({
          ...l,
          quantite: Number(l.quantite),
          prix_unitaire: Number(l.prix_unitaire),
          tva: Number(l.tva),
        })),
      };
      if (isEdit) {
        await api.patch(`/admin/devis/${id}`, payload);
        toast.success("Devis mis à jour");
      } else {
        const { data } = await api.post("/admin/devis", payload);
        toast.success(`Devis ${data.numero} créé`);
      }
      navigate("/admin/devis");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-serial">Chargement</p>;

  return (
    <form onSubmit={submit} data-testid="devis-editor">
      <Link to="/admin/devis" className="text-eyebrow inline-flex items-center gap-2 link-underline-static mb-6">
        <ArrowLeft size={14} /> Retour à la liste
      </Link>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-eyebrow mb-3">Devis</p>
          <h1 className="h-display text-4xl md:text-5xl">{isEdit ? "Éditer un devis." : "Nouveau devis."}</h1>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-7 space-y-5">
          <div>
            <label className="text-eyebrow block mb-2">Client *</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
              required
              data-testid="devis-client"
            >
              <option value="">— Sélectionner un client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom} {c.raison_sociale ? `(${c.raison_sociale})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-eyebrow block mb-2">Titre du devis *</label>
            <input
              required
              value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[15px] outline-none"
              placeholder="Cuisine en L sur-mesure — chêne et laque mate"
              data-testid="devis-titre"
            />
          </div>
          <div>
            <label className="text-eyebrow block mb-2">Objet</label>
            <textarea
              value={form.objet || ""}
              onChange={(e) => setForm({ ...form, objet: e.target.value })}
              className="w-full bg-transparent border border-border focus:border-primary p-3 text-[14px] min-h-[70px]"
              data-testid="devis-objet"
            />
          </div>
        </div>
        <div className="md:col-span-4 md:col-start-9 space-y-5">
          <div>
            <label className="text-eyebrow block mb-2">Validité (jours)</label>
            <input
              type="number"
              value={form.validite_jours}
              onChange={(e) => setForm({ ...form, validite_jours: parseInt(e.target.value || "30", 10) })}
              className="w-full bg-transparent border-b border-border py-2.5 text-[14.5px] outline-none"
              data-testid="devis-validite"
            />
          </div>
        </div>
      </div>

      {/* LIGNES */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-eyebrow">Lignes du devis</p>
          <button type="button" onClick={addLine} className="text-eyebrow text-primary link-underline-static inline-flex items-center gap-1" data-testid="devis-add-line">
            <Plus size={12} /> Ajouter une ligne
          </button>
        </div>
        <div className="space-y-4">
          {form.lignes.map((l, i) => (
            <div key={l._uid || `line-${i}`} className="border border-border p-4 grid md:grid-cols-12 gap-3" data-testid={`devis-line-${i}`}>
              <div className="md:col-span-5">
                <label className="text-serial block mb-1">Désignation</label>
                <input
                  value={l.designation}
                  onChange={(e) => setLine(i, "designation", e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary py-1.5 text-[14px] outline-none"
                  data-testid={`line-designation-${i}`}
                />
                <textarea
                  placeholder="Description (optionnelle)"
                  value={l.description || ""}
                  onChange={(e) => setLine(i, "description", e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary py-1.5 text-[13px] outline-none mt-2 min-h-[40px]"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-serial block mb-1">Qté</label>
                <input
                  type="number"
                  step="0.01"
                  value={l.quantite}
                  onChange={(e) => setLine(i, "quantite", e.target.value)}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-serial block mb-1">Unité</label>
                <input
                  value={l.unite}
                  onChange={(e) => setLine(i, "unite", e.target.value)}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-serial block mb-1">PU HT (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={l.prix_unitaire}
                  onChange={(e) => setLine(i, "prix_unitaire", e.target.value)}
                  className="w-full bg-transparent border-b border-border py-1.5 text-[14px] outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-serial block mb-1">Montant HT</label>
                <div
                  className="w-full border-b border-border py-1.5 text-[14px] text-right tabular-nums text-primary font-medium"
                  data-testid={`line-montant-ht-${i}`}
                >
                  {formatEuro(Number(l.quantite || 0) * Number(l.prix_unitaire || 0))}
                </div>
              </div>
              <div className="md:col-span-1 flex items-end justify-end">
                {form.lignes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`line-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TOTAUX */}
      <div className="mt-10 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7">
          <label className="text-eyebrow block mb-2">Conditions</label>
          <textarea
            value={form.conditions || ""}
            onChange={(e) => setForm({ ...form, conditions: e.target.value })}
            className="w-full bg-transparent border border-border p-3 text-[13px] min-h-[100px]"
            placeholder="Acompte de 30% à la signature, solde à la livraison. Garantie décennale et RC Pro à jour…"
            data-testid="devis-conditions"
          />
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <div className="border-t border-border pt-4 space-y-2 text-right">
            <p className="text-serial">Total</p>
            <p className="text-3xl font-serif text-primary">{formatEuro(totals.ht)}</p>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              TVA non applicable, art. 293 B du CGI
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-4 justify-end">
        <Link to="/admin/devis" className="btn-outline" data-testid="devis-cancel">
          Annuler
        </Link>
        <button type="submit" className="btn-terra" disabled={saving} data-testid="devis-save">
          {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer le devis"}
        </button>
      </div>
    </form>
  );
}
